import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import {
  useAppearanceDraft,
  type AppearanceSection,
  APPEARANCE_SECTION_LABELS,
} from '@/contexts/AppearanceDraftContext';
import {
  useNavigationBlocker,
  type NavigationBlockTransaction,
} from '@/hooks/useNavigationBlocker';
import { cn } from '@/lib/utils';

function formatDirtySectionList(sections: AppearanceSection[]): string {
  return sections.map((s) => APPEARANCE_SECTION_LABELS[s]).join(', ');
}

/** True only for the Configuration Overview settings page (exact path). */
export function isAppearanceSettingsPath(pathname: string): boolean {
  return pathname === '/config/overview';
}

export type ContentDockMetrics = {
  left: number;
  width: number;
};

function docksEqual(a: ContentDockMetrics | null, b: ContentDockMetrics): boolean {
  return a != null && a.left === b.left && a.width === b.width;
}

/**
 * Pin the action bar to the viewport bottom, horizontally aligned to
 * `.admin-main-content` so it does not cover the sidebar. Falls back to
 * inset padding when the layout shell is absent (unit tests).
 *
 * Recomputes via ResizeObserver on the content shell (sidebar width changes
 * without window resize) plus window resize/scroll as fallback.
 */
export function useContentBottomDock(active: boolean): ContentDockMetrics | null {
  const [dock, setDock] = useState<ContentDockMetrics | null>(null);

  useLayoutEffect(() => {
    if (!active) {
      setDock(null);
      return;
    }

    const sync = () => {
      const shell = document.querySelector('.admin-main-content') as HTMLElement | null;
      let next: ContentDockMetrics;
      if (!shell) {
        const pad = 16;
        next = { left: pad, width: Math.max(0, window.innerWidth - pad * 2) };
      } else {
        const rect = shell.getBoundingClientRect();
        const pad = window.matchMedia('(min-width: 1024px)').matches ? 24 : 16;
        next = {
          left: rect.left + pad,
          width: Math.max(0, rect.width - pad * 2),
        };
      }
      setDock((prev) => (docksEqual(prev, next) ? prev : next));
    };

    sync();
    const shell = document.querySelector('.admin-main-content');
    const ro =
      shell && typeof ResizeObserver !== 'undefined' ? new ResizeObserver(sync) : null;
    if (shell && ro) ro.observe(shell);
    window.addEventListener('resize', sync);
    window.addEventListener('scroll', sync, { passive: true });
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', sync);
      window.removeEventListener('scroll', sync);
    };
  }, [active]);

  return dock;
}

function focusLeaveFallback(): void {
  const h1 = document.querySelector('h1') as HTMLElement | null;
  const main =
    (document.querySelector('main') as HTMLElement | null) ||
    (document.querySelector('.admin-main-content') as HTMLElement | null);
  const target = h1 || main;
  if (!target) return;
  if (!target.hasAttribute('tabindex')) {
    target.tabIndex = -1;
  }
  target.focus({ preventScroll: true });
}

/**
 * Appearance action bar — dirty / running / partial / failed / success.
 * Must only mount on `/config/overview` (or be gated by exact pathname).
 * Sole commit path: commitDirtySections().
 *
 * Live-region: Pattern B — polite status for dirty/running/success;
 * single alert for partial/failed (never nested inside status).
 */
export function AppearanceUnsavedStrip() {
  const {
    isDirty,
    dirtyCount,
    dirtySections,
    discardAll,
    saving,
    isCommitting,
    brandingUploadBusy,
    commitResult,
    commitDirtySections,
    clearCommitResult,
  } = useAppearanceDraft();
  const location = useLocation();
  const [discardOpen, setDiscardOpen] = useState(false);
  const [showSuccessBriefly, setShowSuccessBriefly] = useState(false);
  const successTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const discardButtonRef = useRef<HTMLButtonElement | null>(null);

  const isBusy =
    isCommitting ||
    saving.theme ||
    saving.branding ||
    saving.ui ||
    brandingUploadBusy;

  useEffect(() => {
    if (commitResult.status === 'success' && commitResult.succeeded.length > 0) {
      setShowSuccessBriefly(true);
      if (successTimerRef.current) clearTimeout(successTimerRef.current);
      successTimerRef.current = setTimeout(() => {
        setShowSuccessBriefly(false);
        clearCommitResult();
      }, 2500);
    }
    return () => {
      if (successTimerRef.current) clearTimeout(successTimerRef.current);
    };
  }, [commitResult, clearCommitResult]);

  const onSettingsPath = isAppearanceSettingsPath(location.pathname);
  const showPartialOrFailed =
    commitResult.status === 'partial' || commitResult.status === 'failed';
  const visible =
    onSettingsPath &&
    (isDirty || isCommitting || showPartialOrFailed || showSuccessBriefly);

  const dock = useContentBottomDock(visible);

  if (!visible) {
    return null;
  }

  const handleConfirmDiscard = () => {
    discardAll();
    setDiscardOpen(false);
  };

  const handleCommit = () => {
    void commitDirtySections();
  };

  const handleRetry = () => {
    void commitDirtySections();
  };

  const totalForProgress = commitResult.totalCount || dirtyCount || 1;
  const progressValue = isCommitting
    ? Math.round((commitResult.completedCount / totalForProgress) * 100)
    : 0;

  let summary: string;
  if (isCommitting || commitResult.status === 'running') {
    summary = `Đang lưu ${commitResult.completedCount}/${commitResult.totalCount || dirtyCount} nhóm thay đổi`;
  } else if (commitResult.status === 'success' && showSuccessBriefly) {
    summary = `Đã lưu ${commitResult.succeeded.length} nhóm thay đổi`;
  } else if (commitResult.status === 'partial') {
    const failedLabels = commitResult.failed
      .map((f) => APPEARANCE_SECTION_LABELS[f.section])
      .join(', ');
    summary = `Đã lưu ${commitResult.succeeded.length}/${commitResult.attempted.length} nhóm thay đổi. Chưa lưu: ${failedLabels}.`;
  } else if (commitResult.status === 'failed') {
    const failedLabels = commitResult.failed
      .map((f) => APPEARANCE_SECTION_LABELS[f.section])
      .join(', ');
    summary = `Không thể lưu các thay đổi.${failedLabels ? ` Lỗi: ${failedLabels}.` : ''}`;
  } else if (commitResult.status === 'blocked') {
    summary = 'Đang tải lên thương hiệu — vui lòng đợi trước khi lưu.';
  } else {
    const sectionHint =
      dirtySections.length > 0 && dirtySections.length <= 3
        ? ` (${formatDirtySectionList(dirtySections)})`
        : '';
    summary = `Có ${dirtyCount} nhóm thay đổi chưa lưu${sectionHint}`;
  }

  const showRetry =
    !isCommitting &&
    isDirty &&
    (commitResult.status === 'partial' || commitResult.status === 'failed');

  const showSave =
    !isCommitting &&
    isDirty &&
    !showRetry &&
    commitResult.status !== 'running';

  const isErrorTone =
    commitResult.status === 'failed' || commitResult.status === 'partial';

  /** Pattern B: polite status XOR single alert — never nested. */
  const usePoliteStatus = !showPartialOrFailed;
  const failedDetailLines = showPartialOrFailed
    ? commitResult.failed.filter((f) => f.message)
    : [];

  const dockStyle =
    dock != null
      ? {
          position: 'fixed' as const,
          left: dock.left,
          width: dock.width,
          bottom: 'calc(1rem + env(safe-area-inset-bottom, 0px))',
        }
      : undefined;

  return (
    <>
      <div
        className={cn(
          'z-30 w-full max-w-full',
          'rounded-lg border border-border bg-card/95 px-4 py-3 shadow-md backdrop-blur supports-[backdrop-filter]:bg-card/90',
          dock == null && 'sticky bottom-4 mt-6 mb-[env(safe-area-inset-bottom,0px)]',
        )}
        style={dockStyle}
        role={usePoliteStatus ? 'status' : undefined}
        aria-live={usePoliteStatus ? 'polite' : undefined}
        aria-atomic={usePoliteStatus ? true : undefined}
        aria-busy={isCommitting || undefined}
        data-testid="appearance-unsaved-strip"
        data-commit-status={commitResult.status}
        data-dock={dock ? 'fixed-content' : 'sticky-fallback'}
        data-live-mode={usePoliteStatus ? 'status' : 'alert-sibling'}
      >
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0 flex-1 space-y-2">
            {usePoliteStatus ? (
              <p
                className={cn(
                  'text-sm',
                  isErrorTone ? 'text-destructive' : 'text-muted-foreground',
                )}
                data-testid="appearance-commit-summary"
              >
                {summary}
              </p>
            ) : (
              <div
                role="alert"
                data-testid="appearance-commit-alert"
                className="space-y-2"
              >
                <p
                  className="text-sm text-destructive"
                  data-testid="appearance-commit-summary"
                >
                  {summary}
                </p>
                {failedDetailLines.length > 0 && (
                  <ul className="text-xs text-destructive/90 list-disc pl-4">
                    {failedDetailLines.map((f) => (
                      <li key={f.section}>
                        {APPEARANCE_SECTION_LABELS[f.section]}: {f.message}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            {isCommitting && (
              <Progress
                value={progressValue}
                className="h-1.5 w-full max-w-md"
                data-testid="appearance-commit-progress"
              />
            )}
          </div>
          <div
            className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center sm:justify-end shrink-0"
            data-testid="appearance-action-buttons"
          >
            {/* Primary actions first (DOM = visual = tab order). Cancel last. */}
            {showSave && (
              <Button
                type="button"
                size="sm"
                className="w-full sm:w-auto min-h-9"
                disabled={isBusy}
                onClick={handleCommit}
                data-testid="appearance-commit-button"
              >
                Lưu thay đổi
              </Button>
            )}
            {showRetry && (
              <Button
                type="button"
                size="sm"
                className="w-full sm:w-auto min-h-9"
                disabled={isBusy}
                onClick={handleRetry}
                data-testid="appearance-retry-button"
              >
                Thử lại
              </Button>
            )}
            {isCommitting && (
              <Button
                type="button"
                size="sm"
                className="w-full sm:w-auto min-h-9"
                disabled
                data-testid="appearance-commit-button"
              >
                Đang lưu...
              </Button>
            )}
            {isDirty && !(commitResult.status === 'success' && showSuccessBriefly) && (
              <Button
                ref={discardButtonRef}
                type="button"
                variant="outline"
                size="sm"
                className="w-full sm:w-auto min-h-9"
                disabled={isBusy}
                onClick={() => setDiscardOpen(true)}
                data-testid="appearance-discard-all-button"
              >
                Hủy thay đổi
              </Button>
            )}
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={discardOpen}
        onOpenChange={(open) => {
          setDiscardOpen(open);
          if (!open) {
            // Restore focus to the trigger after Escape / cancel (Radix may land on unrelated overlays).
            requestAnimationFrame(() => {
              discardButtonRef.current?.focus();
            });
          }
        }}
        title="Hủy các thay đổi chưa lưu?"
        description="Các thay đổi trong Màu sắc, Thương hiệu và Giao diện sẽ được khôi phục về cấu hình đã lưu gần nhất."
        confirmText="Hủy thay đổi"
        cancelText="Tiếp tục chỉnh sửa"
        variant="destructive"
        disabled={isBusy}
        onConfirm={handleConfirmDiscard}
      />
    </>
  );
}

/**
 * Layout-level navigation guard + leave dialog + beforeunload.
 * Does not render the dirty strip (strip is route-local on Settings).
 */
export function AppearanceUnsavedNavigationGuard({ children }: { children: ReactNode }) {
  const { isDirty, dirtySections, discardAll, saving, isCommitting, brandingUploadBusy } =
    useAppearanceDraft();
  const [leaveOpen, setLeaveOpen] = useState(false);
  const pendingNavigationRef = useRef<NavigationBlockTransaction | null>(null);
  const discardOnceRef = useRef(false);
  const isLeavingRef = useRef(false);
  const leaveTriggerRef = useRef<HTMLElement | null>(null);
  const isAnySaving =
    isCommitting ||
    saving.theme ||
    saving.branding ||
    saving.ui ||
    brandingUploadBusy;

  useNavigationBlocker(isDirty, (tx) => {
    if (isLeavingRef.current) return;
    pendingNavigationRef.current = tx;
    const ae = document.activeElement;
    leaveTriggerRef.current =
      ae instanceof HTMLElement && ae !== document.body ? ae : null;
    setLeaveOpen(true);
  });

  useEffect(() => {
    if (!isDirty) return;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  // After Leave discard, dirty clears — ensure dialog cannot remain open.
  useEffect(() => {
    if (!isDirty && leaveOpen) {
      setLeaveOpen(false);
    }
  }, [isDirty, leaveOpen]);

  const handleStay = () => {
    setLeaveOpen(false);
    if (isLeavingRef.current) return;
    const tx = pendingNavigationRef.current;
    pendingNavigationRef.current = null;
    discardOnceRef.current = false;
    tx?.revert?.();
    const trigger = leaveTriggerRef.current;
    leaveTriggerRef.current = null;
    requestAnimationFrame(() => {
      if (trigger && trigger.isConnected && typeof trigger.focus === 'function') {
        trigger.focus({ preventScroll: true });
        return;
      }
      focusLeaveFallback();
    });
  };

  const handleLeave = () => {
    if (isAnySaving) return;
    isLeavingRef.current = true;
    const tx = pendingNavigationRef.current;
    pendingNavigationRef.current = null;
    leaveTriggerRef.current = null;
    setLeaveOpen(false);
    if (!discardOnceRef.current) {
      discardOnceRef.current = true;
      discardAll();
    }
    discardOnceRef.current = false;
    try {
      tx?.retry();
    } finally {
      // Keep leaving flag until after React applies isDirty=false so onBlock cannot reopen.
      Promise.resolve().then(() => {
        isLeavingRef.current = false;
      });
    }
  };

  const leaveDescription =
    dirtySections.length > 0
      ? `Các thay đổi chưa lưu trong ${formatDirtySectionList(dirtySections)} sẽ bị mất nếu bạn rời trang.`
      : 'Các thay đổi Appearance chưa lưu sẽ bị mất nếu bạn rời trang.';

  return (
    <>
      {children}
      <ConfirmDialog
        open={leaveOpen}
        onOpenChange={(open) => {
          if (!open) handleStay();
        }}
        title="Bạn có thay đổi chưa lưu"
        description={leaveDescription}
        confirmText="Rời trang"
        cancelText="Ở lại"
        variant="destructive"
        disabled={isAnySaving}
        onConfirm={handleLeave}
      />
    </>
  );
}

/** @deprecated Prefer AppearanceUnsavedNavigationGuard + AppearanceUnsavedStrip */
export function AppearanceUnsavedChangesHost({ children }: { children: ReactNode }) {
  return (
    <AppearanceUnsavedNavigationGuard>
      <AppearanceUnsavedStrip />
      {children}
    </AppearanceUnsavedNavigationGuard>
  );
}
